import React, { useMemo } from "react"
import { AbsoluteFill, spring, useCurrentFrame, useVideoConfig } from "remotion"
import { loadFont } from "../../utils/fonts"
import type { Track, CaptionStyleConfig, SceneMetadata } from "../../schemas"
import { getCaptionDisplayModel, resolveCaptionStyleConfig } from "@/lib/caption-display"
import {
  ACTIVE_WORD_SCALE,
  captionWordGapPx,
  estimateCharsPerLine,
  getChunkEmoji,
  isKeywordWord,
  resolveCaptionCenter,
  resolveKeywordColor,
} from "@/lib/caption-layout"
import {
  activeCaptionWordIndex,
  captionGroupLimits,
  chunkCaptionWords,
  displayCaptionText,
  timeCaptionGroups,
} from "@/lib/caption-utils"
import { layoutChunkAt, resolveLayoutSpans } from "../../utils/layout"
import { maskProfanity } from "@/lib/profanity"

interface CaptionTrackProps {
  track: Track
  sceneMetadata?: SceneMetadata
}

export const CaptionTrack: React.FC<CaptionTrackProps> = ({ track, sceneMetadata }) => {
  const frame = useCurrentFrame()
  const { fps, durationInFrames, width: compositionWidth, height: compositionHeight } = useVideoConfig()
  const styleConfig: CaptionStyleConfig = resolveCaptionStyleConfig(track.styleConfig)
  // Auto captions dodge the layout as drawn (short scenes hold their neighbour's layout), not the
  // raw scene list, so they don't hop up and down on scenes the picture never switches for.
  const layoutSpans = useMemo(
    () => (sceneMetadata?.chunks?.length ? resolveLayoutSpans(sceneMetadata, fps, durationInFrames) : []),
    [sceneMetadata, fps, durationInFrames],
  )

  // Load the configured font
  loadFont(styleConfig.font.family)

  // Flatten all text elements from all sections/segments
  const allTextElements = useMemo(() => {
    const elements: Array<{
      id: string
      text: string
      timelineIn: number
      timelineOut: number
      color: number
    }> = []
    for (const section of track.sections) {
      for (const segment of section.segments) {
        const content = segment.content as { textElements?: Array<{
          id: string
          text: string
          timeline: { in: number; out: number }
          color?: number
          deleted?: boolean
        }> } | undefined
        if (content?.textElements) {
          for (const el of content.textElements) {
            if (!el.deleted) {
              elements.push({
                id: el.id,
                text: el.text,
                timelineIn: el.timeline.in,
                timelineOut: el.timeline.out,
                color: el.color ?? 0,
              })
            }
          }
        }
      }
    }
    return elements
  }, [track.sections])

  // Words are timed by their start (timeline.in) and real end (timeline.out); the shared chunker
  // groups them and timeCaptionGroups keeps each group up until the next one when the gap is short.
  const maxLines = styleConfig.maxLines ?? null
  const charsPerLine = useMemo(() => {
    if (!maxLines) return undefined
    const sizing = getCaptionDisplayModel(styleConfig, {
      mode: "render",
      compositionWidth,
      compositionHeight,
      wordsInGroup: captionGroupLimits(styleConfig.wordsPerCaption, maxLines).maxWords,
    })
    return estimateCharsPerLine(
      (compositionWidth * sizing.captionWidthPct) / 100,
      sizing.fontSize,
      styleConfig.textTransform === "uppercase",
    )
  }, [maxLines, styleConfig, compositionWidth, compositionHeight])
  const captionGroups = useMemo(() => {
    const sortedElements = [...allTextElements].sort((left, right) => left.timelineIn - right.timelineIn)
    const groups = chunkCaptionWords(
      sortedElements,
      captionGroupLimits(styleConfig.wordsPerCaption, maxLines, charsPerLine),
    )
    const timings = timeCaptionGroups(groups)
    return groups.map((words, index) => ({ words, ...timings[index] }))
  }, [allTextElements, styleConfig.wordsPerCaption, maxLines, charsPerLine])

  // Find the active caption group at the current frame
  const currentTimeMs = (frame / fps) * 1000
  const activeCaption = captionGroups.find(
    (group) => currentTimeMs >= group.startMs && currentTimeMs < group.endMs,
  )
  if (!activeCaption) return null
  const activeGroup = activeCaption.words
  const activeGroupStartMs = activeCaption.startMs
  const activeGroupStartFrame = Math.max(0, Math.floor((activeGroupStartMs / 1000) * fps))
  const relativeFrame = Math.max(0, frame - activeGroupStartFrame)
  const enter = spring({
    frame: relativeFrame,
    fps,
    config: {
      damping: 200,
    },
    durationInFrames: 5,
  })

  const animationStyle = styleConfig.animation
  const entranceTranslateY = animationStyle === "slide" || animationStyle === "deep-diver"
    ? (1 - enter) * 36
    : animationStyle === "pop"
      ? (1 - enter) * 12
      : 0
  const entranceScale = animationStyle === "pop" || animationStyle === "scale" || animationStyle === "karaoke"
    ? 0.9 + (0.1 * enter)
    : 1
  const entranceOpacity = animationStyle === "fade" || animationStyle === "deep-diver"
    ? Math.min(1, 0.25 + enter)
    : 1

  const center = resolveCaptionCenter(
    styleConfig,
    styleConfig.position === "auto" ? layoutChunkAt(layoutSpans, activeGroupStartFrame) : null,
  )
  const keywords = styleConfig.keywordHighlightEnabled ? track.aiCaptions?.keywords : undefined
  const keywordColor = resolveKeywordColor(styleConfig)
  const emoji = styleConfig.aiEmojiEnabled ? getChunkEmoji(activeGroup, track.aiCaptions?.emojis) : null
  const singleLine = maxLines === 1

  const hasLongWord = activeGroup.some((word) => word.text.length >= 9)
  const colorMap = [styleConfig.colors.default, styleConfig.colors.primary, styleConfig.colors.secondary]
  const display = getCaptionDisplayModel(styleConfig, {
    mode: "render",
    compositionWidth,
    compositionHeight,
    wordsInGroup: activeGroup.length,
    hasLongWord,
  })
  const captionWidth = `${display.captionWidthPct}%`

  // Gaps are in caption pixels: em on the flex box would resolve against the inherited 16px.
  const wordGapPx = captionWordGapPx(
    display.fontSize,
    styleConfig.stroke.enabled ? display.strokeWidth + 0.8 : 0,
    Math.max(...activeGroup.map((word) => word.text.length)),
    styleConfig.textTransform === "uppercase",
  )

  const textTransform = styleConfig.textTransform as React.CSSProperties["textTransform"]
  const activeWordIndex = activeCaptionWordIndex(activeGroup, currentTimeMs, activeCaption.endMs)

  return (
    <AbsoluteFill>
      {/* One element per caption: the next caption appears in its own place instead of moving this one. */}
      <div
        key={activeGroupStartMs}
        style={{
          position: "absolute",
          left: `${center.x * 100}%`,
          top: `${center.y * 100}%`,
          width: singleLine ? "max-content" : captionWidth,
          textAlign: "center",
          transform: `translate(-50%, -50%) translateY(${entranceTranslateY}px) scale(${entranceScale})`,
          opacity: entranceOpacity,
        }}
      >
        {emoji && (
          <div
            style={{
              position: "absolute",
              left: "50%",
              bottom: "100%",
              fontSize: display.fontSize * 1.5,
              lineHeight: 1,
              transform: `translateX(-50%) scale(${enter})`,
              marginBottom: display.fontSize * 0.15,
            }}
          >
            {emoji}
          </div>
        )}
        <div
          style={{
            display: "inline-flex",
            flexWrap: singleLine ? "nowrap" : "wrap",
            justifyContent: "center",
            gap: `${display.fontSize * 0.18}px ${wordGapPx}px`,
            lineHeight: display.lineHeight,
            maxWidth: "100%",
            backgroundColor: display.backdrop.enabled ? display.backdrop.color : "transparent",
            borderRadius: display.backdrop.enabled ? display.backdrop.radius : 0,
            padding: display.backdrop.enabled
              ? `${display.backdrop.paddingY} ${display.backdrop.paddingX}`
              : 0,
          }}
        >
          {activeGroup.map((element, index) => {
            const isActive = styleConfig.activeWordEnabled && index === activeWordIndex

            const wordColor = element.color > 0
              ? colorMap[element.color] ?? display.defaultColor
              : isActive
                ? display.primaryColor
                : isKeywordWord(element.timelineIn, keywords)
                  ? keywordColor
                  : display.defaultColor

            const activeWordBackground = styleConfig.animation === "karaoke-bg" && isActive
              ? display.activeBackgroundColor
              : "transparent"
            const activeStrokeWidth = isActive
              ? Math.max(display.strokeWidth, display.strokeWidth + 0.8)
              : display.strokeWidth
            const karaokeBgWordColor =
              styleConfig.animation === "karaoke-bg" && isActive
                ? display.activeTextColor
                : wordColor

            return (
              <span
                key={element.id}
                style={{
                  fontFamily: styleConfig.font.family,
                  fontSize: display.fontSize,
                  fontWeight: styleConfig.font.weight,
                  fontStyle: styleConfig.font.style,
                  color: karaokeBgWordColor,
                  textTransform,
                  textShadow: display.textShadow,
                  backgroundColor: activeWordBackground,
                  borderRadius: activeWordBackground === "transparent" ? 0 : 8,
                  padding: activeWordBackground === "transparent" ? 0 : "0.04em 0.16em",
                  WebkitTextStroke: styleConfig.stroke.enabled
                    ? `${activeStrokeWidth}px ${styleConfig.stroke.color}`
                    : undefined,
                  paintOrder: "stroke fill",
                  // No CSS transition: a render screenshots each frame in wall-clock time, so a fade
                  // would leave the previous word lit for several frames next to the new one.
                  transform: isActive ? `scale(${ACTIVE_WORD_SCALE})` : "scale(1)",
                  letterSpacing: display.letterSpacing,
                  wordSpacing: "0.15em",
                  textRendering: "optimizeLegibility",
                  WebkitFontSmoothing: "antialiased",
                  filter: isActive ? "saturate(1.08)" : "none",
                }}
              >
                {styleConfig.maskProfanity
                  ? maskProfanity(displayCaptionText(element.text))
                  : displayCaptionText(element.text)}
              </span>
            )
          })}
        </div>
      </div>
    </AbsoluteFill>
  )
}
